import type { OnlineApplicationCompanyArgs } from './online_application_company';
import type { OnlineApplicationBillingArgs } from './online_application_billing';
import type { OnlineApplicationPlanArgs } from './online_application_plan';
import type { OnlineApplicationOptionArgs } from './online_application_option';
import type { OnlineApplicationApprovalLogArgs } from './online_application_approval_log';

// 共通のAPIレスポンス型
export interface ApplicationData {
  id: number;
  status: string;
  company_name: string;
  company_name_kana: string;
  contact_name: string;
  contact_email: string;
  expiration_date?: string;
  applied_at?: string;
  approved_at?: string;
  companies: OnlineApplicationCompanyArgs[];
  billing: OnlineApplicationBillingArgs;
  plans: OnlineApplicationPlanArgs[];
  options: OnlineApplicationOptionArgs[];
  is_billing_same_as_company: boolean;
  start_desired_month?: string;
  potential_gmv?: number;
  salesforce_url?: string;
  slack_url?: string;
  application_date?: string;
  remarks?: string;
  approval_logs?: OnlineApplicationApprovalLogArgs[];
  applicant_id?: number;
  applicant_name?: string;
  application_email_address?: string;
  email_sent_at?: string;
  created_at: string;
  updated_at: string;
}

// ステータスの型定義
export type OnlineApplicationStatus =
  | 'draft'
  | 'admin_applied'
  | 'first_approved'
  | 'final_approved'
  | 'sent_back'
  | 'withdraw'
  | 'send_email'
  | 'user_applied';

// 共通のエラーメッセージ
export const ERROR_MESSAGES = {
  FETCH_FAILED: '申込情報の取得に失敗しました',
  SAVE_FAILED: '保存に失敗しました',
  SUBMIT_FAILED: '申請に失敗しました',
  VALIDATION_FAILED: '入力内容に誤りがあります',
  REQUIRED_FIELD: '必須項目です',
  INVALID_EMAIL: 'メールアドレスの形式が正しくありません',
  INVALID_PHONE: '電話番号の形式が正しくありません',
  CONTACT_SUPPORT: 'お手数ですが、担当者までご連絡くださいますようお願いいたします。'
} as const;

// APIエンドポイント
export const API_ENDPOINTS = {
  USER: {
    GET_BY_TOKEN: (token: string) => `/online_applications/token/${token}.json`,
    UPDATE: (token: string) => `/online_applications/token/${token}`,
    COMPLETE: (token: string) => `/online_applications/complete/${token}.json`
  },
  ADMIN: {
    LIST: '/admin/online_applications',
    NEW: '/admin/online_applications/new.json',
    CREATE: '/admin/online_applications',
    GET: (id: number) => `/admin/online_applications/${id}.json`,
    UPDATE: (id: number) => `/admin/online_applications/${id}`,
    PREVIEW: (id: number) => `/admin/online_applications/${id}/preview.json`,
    ADMIN_APPLY: (id: number) => `/admin/online_applications/${id}/admin_apply`,
    FIRST_APPROVE: (id: number) => `/admin/online_applications/${id}/first_approve`,
    FINAL_APPROVE: (id: number) => `/admin/online_applications/${id}/final_approve`,
    SEND_BACK: (id: number) => `/admin/online_applications/${id}/send_back`,
    WITHDRAW: (id: number) => `/admin/online_applications/${id}/withdraw`,
    SEND_EMAIL: (id: number) => `/admin/online_applications/${id}/send_email`
  }
} as const;

// 画面遷移用のパス
export const PAGE_PATHS = {
  ADMIN: {
    LIST: '/admin/online_applications',
    NEW: '/admin/online_applications/new',
    EDIT: (id: number) => `/admin/online_applications/${id}/edit`,
    DETAIL: (id: number) => `/admin/online_applications/${id}`,
    CONFIRM: '/admin/online_applications/confirm',
    PREVIEW: (id: number) => `/admin/online_applications/${id}/preview`
  }
} as const;
